
import { Component, Prop, Vue } from "vue-property-decorator";
import VueTreeselect from "@riophae/vue-treeselect";
import api from "@/services/api.service";
import EventCategory from "@/types/sv-data/events/EventCategory";

	@Component({
		components: {
			"vue-treeselect": VueTreeselect
		}
	})
	export default class EventOutcomeMultiSelect extends Vue {
		private eventCategoriesTree: EventCategory[] = [];
		private isLoading: boolean = true;
		private previousList: number[] = [];
		private newList: number[] = [];

		// Prop required by generic table to pass selected value down
		// to this component.
		@Prop()
		public value: number[];

		// Prop to define if only activity log outcomes should be shown.
		@Prop({ default: false })
		public isActivityLogOutcomes: boolean;

		// Prop to define if only alarm outcomes should be shown.
		@Prop({ default: false })
		public isAlarmOutcomes: boolean;

		// Prop to determine if child outcomes should also be selected when the parent is selected
		@Prop({ default: false })
		public selectChildOutcomes: boolean;

		public reset(): void {
			this.newList = [];
			this.previousList = [];
			this.$emit("input", this.newList);
		}

		private async beforeMount(): Promise<void> {
			try {
				this.previousList = this.value;
				// Clear array to prevent invalid values being displayed, while awaiting API call.
				this.newList = [];
				this.eventCategoriesTree = await api.fetchEventOutcomeTree();

				// Remove any previously selected values that are not intended to be shown.
				if (this.isActivityLogOutcomes) {
					this.eventCategoriesTree = this.filterEventTree(this.eventCategoriesTree, (r) => r.isActivityLog === true);
				} else if (this.isAlarmOutcomes) {
					this.eventCategoriesTree = this.filterEventTree(this.eventCategoriesTree,(r) => r.isAlarm === true);
				}

				const validIDs = this.flattenTreeIDs(this.eventCategoriesTree);
				this.previousList = this.previousList?.filter((id) => validIDs.includes(id))

				// Reapply values after api call to be shown.
				this.newList = this.previousList;
				this.$emit("input", this.newList);
			} catch (err) {
				console.error("Failed to retrieve EventOutomeCategories", err);
			} finally {
				this.isLoading = false;
			}
		}

		private beforeDestroy(): void {
			this.eventCategoriesTree = [];
			this.newList = [];
			this.previousList = [];
		}

		private filterEventTree(tree: EventCategory[], predicate: (node: EventCategory) => boolean): EventCategory[] {
			return tree.reduce((acc: EventCategory[], node: EventCategory) => {
				// Filter this node’s childOutcomes first (if any)
				const filteredChildren = node.childOutcomes	? this.filterEventTree(node.childOutcomes, predicate) : [];

				// If this node itself passes the predicate OR if it has filtered children, then keep it
				if (predicate(node) || filteredChildren.length) {
					acc.push({
						...node,
						childOutcomes: filteredChildren
					});
				}

				return acc;
			}, []);
		}

		private flattenTreeIDs(tree: EventCategory[]): number[] {
			return tree.reduce((acc: number[], node) => {
				acc.push(node.eventOutcomeID);
				if (node.childOutcomes && node.childOutcomes.length) {
					acc.push(...this.flattenTreeIDs(node.childOutcomes));
				}
				return acc;
			}, []);
		}

		private normalizer(e: any): any {
			// Case for hiding the database ID in case value has been deleted after being selected
			if (!e.hasOwnProperty("eventOutcomeID")) {
				e.id = null;
				e.label = "This outcome may have been deleted or disabled. Please select another.";
			} else {
				e.id = e.eventOutcomeID;
				e.label = e.title;
				e.children = e.childOutcomes;
			}
			return e;
		}

		private getAllDescendants(node: EventCategory): number[] {
			let ids: number[] = [];

			if (node.childOutcomes && node.childOutcomes.length) {
				node.childOutcomes.forEach((child) => {
					ids.push(child.eventOutcomeID);
					// Recurse to grab grandchildren, great-grandchildren, etc.
					ids.push(...this.getAllDescendants(child));
				});
			}
			return ids;
		}

		private getActivityLogIds = (objects: EventCategory[], filterList: number []) => {
			const activityIds: number[] = [];

			const traverse = (categoryArray: EventCategory[]) => {
				categoryArray.forEach(cat => {
					if (filterList.includes(cat.eventOutcomeID)) {
						activityIds.push(cat.eventOutcomeID);
					}
					if (Array.isArray(cat.childOutcomes) && cat.childOutcomes.length > 0) {
						traverse(cat.childOutcomes);
					}
				});
			};

			traverse(objects);
			return activityIds;
		};

		private onInputChanged(input: number[]): void {
			let selectedOutcomes = [...input];

			if (this.selectChildOutcomes) {
				const oldSelected = this.newList || [];

				const newlySelectedParents = selectedOutcomes
					.filter(outcomeId => !oldSelected.includes(outcomeId))
					.map(outcomeId => this.eventCategoriesTree.find(r => r.eventOutcomeID === outcomeId))
					.filter(parent => parent && parent.childOutcomes);

				newlySelectedParents.forEach(parent => {
					if (parent && parent.childOutcomes) {
						const allDescendantIDs = this.getAllDescendants(parent);

						allDescendantIDs.forEach(id => {
							if (!selectedOutcomes.includes(id)) {
								selectedOutcomes.push(id);
							}
						});
					}
				});

			} else {
				this.newList = selectedOutcomes;
			}

			this.$emit("input", selectedOutcomes, this.getActivityLogIds(this.eventCategoriesTree.filter(r => r.isActivityLog == true), selectedOutcomes));
		}
	}
